import React, { useState, useEffect } from 'react';
import { Printer, Calendar, Smartphone, ExternalLink, RefreshCw } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_BASE_URL ||
  (typeof window !== 'undefined' && window.location?.origin && window.location.origin.includes('milkpromitra')
    ? `${window.location.origin}/api`
    : 'https://milkpromitra.esensevvg.online/api');

/**
 * Public Mobile-First Digital Bill Invoice Viewer
 * Accessible directly by customers via WhatsApp link (No login required)
 * Features:
 *  - 100% Dynamic Client UPI ID & Pre-filled Pay link
 *  - Real-time Scannable UPI QR Code for exact bill amount
 *  - Complete Day-by-day Milk Delivery Calendar Grid
 */
export default function PublicInvoiceView({ lang = 'mr' }) {
  const isMr = lang === 'mr';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [showCalendar, setShowCalendar] = useState(true);

  // Helper to extract query parameter from either hash or window search
  const getParam = (nameList, defaultVal = '') => {
    const hash = window.location.hash || '';
    const hashQuery = hash.includes('?') ? hash.substring(hash.indexOf('?') + 1) : '';
    const search = window.location.search ? window.location.search.replace(/^\?/, '') : '';
    const p1 = new URLSearchParams(hashQuery);
    const p2 = new URLSearchParams(search);
    for (const name of nameList) {
      const v = p1.get(name) || p2.get(name);
      if (v) return v;
    }
    return defaultVal;
  };

  const slug = getParam(['slug', 'tenant', 't']);
  const custId = getParam(['customerId', 'billId', 'cust', 'c']);
  const month = getParam(['month', 'm'], '2026-10');

  useEffect(() => {
    const fetchPublicInvoice = async () => {
      setLoading(true);
      setError(null);

      if (!slug) {
        setError(isMr ? 'डेअरीची माहिती उपलब्ध नाही. कृपया लिंक पुन्हा तपासा.' : 'Dairy information missing in invoice link.');
        setLoading(false);
        return;
      }

      if (!custId) {
        setError(isMr ? 'ग्राहक पावती क्रमांक उपलब्ध नाही.' : 'Customer ID missing in invoice link.');
        setLoading(false);
        return;
      }

      try {
        // 1. Fetch Tenant Info (Dynamic Brand, Address, Phone, UPI ID)
        try {
          const tenantRes = await fetch(`${API_BASE}/tenant/by-slug/${slug}`);
          if (tenantRes.ok) {
            const tenantJson = await tenantRes.json();
            if (tenantJson.success && tenantJson.tenant) {
              setTenantInfo(tenantJson.tenant);
            }
          }
        } catch (e) {
          console.warn('Tenant profile fetch failed:', e);
        }

        // 2. Fetch Customer Billing Info
        const billUrl = month
          ? `${API_BASE}/tenant/${slug}/billing/monthly-summary?year_month=${month}`
          : `${API_BASE}/tenant/${slug}/billing/monthly-summary`;

        const billRes = await fetch(billUrl);
        if (!billRes.ok) {
          throw new Error(isMr ? 'बिल माहिती सर्व्हरवरून लोड करता आली नाही.' : 'Unable to load billing data from server.');
        }

        const billJson = await billRes.json();

        if (billJson.success && Array.isArray(billJson.billing_records)) {
          const rec = billJson.billing_records.find(r => String(r.customerId) === String(custId));
          if (rec) {
            setInvoiceData(rec);
          } else {
            setError(isMr ? 'या ग्राहकाचे या महिन्याचे बिल रेकॉर्ड सापडले नाही.' : 'Customer billing record not found for this month.');
          }
        } else {
          setError(isMr ? 'बिल डेटा उपलब्ध नाही.' : 'Unable to load billing records.');
        }
      } catch (err) {
        setError(err.message || (isMr ? 'बिल पावती उघडताना त्रुटी आली.' : 'Error loading invoice.'));
      } finally {
        setLoading(false);
      }
    };

    fetchPublicInvoice();
  }, [slug, custId, month, isMr]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', flexDirection: 'column', gap: 12 }}>
        <RefreshCw className="animate-spin" size={32} color="#0284c7" />
        <div style={{ fontWeight: 700, color: '#0f172a' }}>
          {isMr ? 'डिजिटल पावती लोड होत आहे...' : 'Loading Digital Invoice...'}
        </div>
      </div>
    );
  }

  if (error || !invoiceData) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', padding: '1rem' }}>
        <div style={{ background: '#ffffff', padding: '2rem', borderRadius: 16, maxWidth: 420, width: '100%', textAlign: 'center', border: '1px solid #fee2e2', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>⚠️</div>
          <h3 style={{ color: '#ef4444', fontWeight: 800, margin: '0 0 0.5rem 0', fontSize: '1.25rem' }}>
            {isMr ? 'पावती सापडली नाही' : 'Invoice Not Found'}
          </h3>
          <p style={{ color: '#64748b', fontSize: '0.85rem', margin: '0.5rem 0 0 0', lineHeight: 1.5 }}>
            {error || (isMr ? 'कृपया लिंक योग्य असल्याची खात्री करा.' : 'Please verify the link.')}
          </p>
        </div>
      </div>
    );
  }

  // Dynamic Brand & UPI Details from Tenant Profile
  const brandName = isMr 
    ? (tenantInfo?.brand_name_mr || tenantInfo?.brandMarathi || tenantInfo?.name || 'दूध संकलन केंद्र')
    : (tenantInfo?.brand_name_en || tenantInfo?.brandEnglish || tenantInfo?.name || 'Milk Collection Center');

  const safeCustId = String(invoiceData.customerId || '1');
  const upiId = tenantInfo?.dairy_settings?.upi_id || tenantInfo?.upi_id || 'dairy@upi';
  const customerName = isMr ? invoiceData.customerName : (invoiceData.customerNameEn || invoiceData.customerName);
  const totalAmount = invoiceData.totalBill;

  // Month Display
  const [yr, mo] = (month || '2026-10').split('-');
  const monthNamesMr = {
    '01': 'जानेवारी', '02': 'फेब्रुवारी', '03': 'मार्च', '04': 'एप्रिल',
    '05': 'मे', '06': 'जून', '07': 'जुलै', '08': 'ऑगस्ट',
    '09': 'सप्टेंबर', '10': 'ऑक्टोबर', '11': 'नोव्हेंबर', '12': 'डिसेंबर'
  };
  const monthNamesEn = {
    '01': 'January', '02': 'February', '03': 'March', '04': 'April',
    '05': 'May', '06': 'June', '07': 'July', '08': 'August',
    '09': 'September', '10': 'October', '11': 'November', '12': 'December'
  };
  const monthLabel = isMr ? `${monthNamesMr[mo] || mo} ${yr}` : `${monthNamesEn[mo] || mo} ${yr}`;

  // Dynamic UPI Intent URL (with exact pre-filled payee, amount & remarks)
  const upiPayUrl = `upi://pay?pa=${encodeURIComponent(upiId)}&pn=${encodeURIComponent(brandName)}&am=${totalAmount}&cu=INR&tn=${encodeURIComponent('Milk Bill ' + customerName + ' ' + (mo ? mo + '/' + yr : ''))}`;
  
  // Scannable UPI QR Code (Generated for exact amount & payee)
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(upiPayUrl)}&size=180x180&margin=6`;

  const calendarDays = invoiceData.calendar || [];
  const vacationDays = calendarDays.filter(d => d.status === 'ABSENT' || d.status === 'VACATION').length;

  return (
    <div style={{ minHeight: '100vh', background: '#0f172a', padding: '1rem 0.5rem', fontFamily: 'system-ui, sans-serif', color: '#0f172a' }}>
      
      {/* Top Action Bar (No Print) */}
      <div className="no-print" style={{ maxWidth: 540, margin: '0 auto 0.75rem auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 0.5rem' }}>
        <span style={{ fontSize: '0.82rem', fontWeight: 800, color: '#38bdf8', display: 'flex', alignItems: 'center', gap: 6 }}>
          🥛 <span>MilkPro Mitra Digital Bill</span>
        </span>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button
            onClick={() => window.print()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              padding: '0.4rem 0.75rem',
              borderRadius: 8,
              border: 'none',
              background: 'rgba(255,255,255,0.15)',
              color: '#ffffff',
              fontSize: '0.78rem',
              fontWeight: 700,
              cursor: 'pointer'
            }}
          >
            <Printer size={14} />
            <span>{isMr ? 'प्रिंट / PDF' : 'Print'}</span>
          </button>
        </div>
      </div>

      {/* Main Digital Bill Card */}
      <div style={{
        maxWidth: 540,
        margin: '0 auto',
        background: '#ffffff',
        borderRadius: 20,
        overflow: 'hidden',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.3)'
      }}>
        
        {/* Dairy Brand Header */}
        <div style={{
          background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
          color: '#ffffff',
          padding: '1.5rem 1.25rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.9 }}>
            ✨ {isMr ? 'अधिकृत दूध बिल पावती' : 'Official Milk Invoice'} ✨
          </div>
          <h1 style={{ fontSize: '1.4rem', fontWeight: 900, margin: '0.4rem 0 0.2rem 0', letterSpacing: '-0.02em' }}>
            {brandName}
          </h1>
          <div style={{ fontSize: '0.78rem', opacity: 0.85 }}>
            {tenantInfo?.city || ''} {tenantInfo?.phone ? `• फोन: ${tenantInfo.phone}` : ''}
          </div>
        </div>

        {/* Invoice Body */}
        <div style={{ padding: '1.25rem' }}>
          
          {/* Bill No & Month */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '0.75rem', borderBottom: '1px solid #e2e8f0' }}>
            <span style={{ background: '#f1f5f9', padding: '3px 8px', borderRadius: 6, fontWeight: 800, fontSize: '0.75rem', fontFamily: 'monospace', color: '#334155' }}>
              #MPM-{yr}-{mo}-${safeCustId.padStart(4, '0')}
            </span>
            <div style={{ fontSize: '0.8rem', fontWeight: 800, color: '#0284c7' }}>
              {isMr ? `महिना: ${monthLabel}` : `Month: ${monthLabel}`}
            </div>
          </div>

          {/* Customer Details */}
          <div style={{ padding: '0.85rem 0', borderBottom: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '0.68rem', fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>
              {isMr ? 'ग्राहक तपशील' : 'Customer Info'}
            </div>
            <div style={{ fontSize: '1.1rem', fontWeight: 900, color: '#0f172a', marginTop: 2 }}>
              {customerName}
            </div>
            <div style={{ fontSize: '0.8rem', color: '#475569', marginTop: 2 }}>
              {invoiceData.address} {invoiceData.phone ? `• ${invoiceData.phone}` : ''}
            </div>
            {invoiceData.lineName && (
              <div style={{ fontSize: '0.75rem', color: '#0284c7', fontWeight: 700, marginTop: 3 }}>
                {isMr ? 'मार्ग: ' : 'Route: '}{invoiceData.lineName}
              </div>
            )}
          </div>

          {/* Summary Metric Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', margin: '1rem 0' }}>
            <div style={{ background: '#f0f9ff', padding: '0.85rem', borderRadius: 12, border: '1px solid #bae6fd', textAlign: 'center' }}>
              <div style={{ fontSize: '0.72rem', color: '#0369a1', fontWeight: 700 }}>
                {isMr ? 'एकूण दूध' : 'Total Milk'}
              </div>
              <div style={{ fontSize: '1.45rem', fontWeight: 900, color: '#0284c7', marginTop: 2 }}>
                {invoiceData.totalMilkLiters} <span style={{ fontSize: '0.85rem' }}>L</span>
              </div>
            </div>

            <div style={{ background: '#f0fdf4', padding: '0.85rem', borderRadius: 12, border: '1px solid #bbf7d0', textAlign: 'center' }}>
              <div style={{ fontSize: '0.72rem', color: '#15803d', fontWeight: 700 }}>
                {isMr ? 'दूध वाटप दिवस' : 'Delivered Days'}
              </div>
              <div style={{ fontSize: '1.45rem', fontWeight: 900, color: '#16a34a', marginTop: 2 }}>
                {invoiceData.deliveredDays} <span style={{ fontSize: '0.85rem' }}>{isMr ? 'दिवस' : 'Days'}</span>
              </div>
            </div>
          </div>

          {/* ========================================================= */}
          {/* MONTHLY ATTENDANCE & DELIVERY CALENDAR VIEW */}
          {/* ========================================================= */}
          {calendarDays.length > 0 && (
            <div style={{
              margin: '1.25rem 0',
              border: '1.5px solid #e2e8f0',
              borderRadius: 14,
              padding: '0.85rem',
              background: '#f8fafc'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.65rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 800, fontSize: '0.85rem', color: '#0f172a' }}>
                  <Calendar size={16} color="#0284c7" />
                  <span>{isMr ? 'मासिक दूध वाटप दिनदर्शिका' : 'Monthly Milk Attendance Calendar'}</span>
                </div>
                <button
                  type="button"
                  onClick={() => setShowCalendar(!showCalendar)}
                  className="no-print"
                  style={{ background: 'none', border: 'none', color: '#0284c7', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer' }}
                >
                  {showCalendar ? (isMr ? 'लपवा' : 'Hide') : (isMr ? 'पहा' : 'Show')}
                </button>
              </div>

              <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '0.65rem' }}>
                {isMr 
                  ? `वाटप: ${invoiceData.deliveredDays} दिवस • सुट्टी/खाडा: ${vacationDays} दिवस`
                  : `Delivered: ${invoiceData.deliveredDays} days • Vacation/Absent: ${vacationDays} days`}
              </div>

              {showCalendar && (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(6, 1fr)',
                  gap: 4
                }}>
                  {calendarDays.map((cd) => {
                    const isDelivered = cd.status === 'DELIVERED';
                    return (
                      <div
                        key={cd.day}
                        style={{
                          borderRadius: 6,
                          padding: '4px 2px',
                          textAlign: 'center',
                          background: isDelivered ? '#ecfdf5' : '#fef2f2',
                          border: isDelivered ? '1px solid #a7f3d0' : '1px solid #fecaca',
                          color: isDelivered ? '#065f46' : '#991b1b'
                        }}
                        title={`दिनांक ${cd.day}: ${isDelivered ? cd.total_qty + 'L दूध' : 'खाडा'}`}
                      >
                        <div style={{ fontSize: '0.68rem', fontWeight: 800 }}>{cd.day}</div>
                        <div style={{ fontSize: '0.62rem', fontWeight: 700, marginTop: 1 }}>
                          {isDelivered ? `${cd.total_qty}L` : (isMr ? 'खाडा' : 'Off')}
                        </div>
                        {cd.has_extra && (
                          <span style={{ fontSize: '0.55rem' }} title="इतर उत्पादने">📦</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Detailed Bill Breakdown */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', padding: '0.85rem 0', borderTop: '1px solid #e2e8f0', borderBottom: '2px solid #0f172a' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
              <span style={{ color: '#475569' }}>
                {isMr ? `दूध रक्कम (${invoiceData.deliveredDays} दिवस)` : `Milk Amount (${invoiceData.deliveredDays} days)`}
              </span>
              <span style={{ fontWeight: 800, color: '#0f172a' }}>₹{invoiceData.milkAmount.toLocaleString()}</span>
            </div>

            {invoiceData.extraAmount > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#16a34a' }}>
                <span>{isMr ? 'उप-उत्पादने / इतर खरेदी' : 'Byproducts & Extra Products'}</span>
                <span style={{ fontWeight: 800 }}>+₹{invoiceData.extraAmount.toLocaleString()}</span>
              </div>
            )}

            {invoiceData.prevBalance > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#d97706' }}>
                <span>{isMr ? 'मागील शिल्लक बाकी' : 'Previous Due Balance'}</span>
                <span style={{ fontWeight: 800 }}>+₹{invoiceData.prevBalance.toLocaleString()}</span>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, paddingTop: 6, borderTop: '1px dashed #cbd5e1' }}>
              <span style={{ fontSize: '1.05rem', fontWeight: 900, color: '#0f172a' }}>
                {isMr ? 'एकूण देय रक्कम' : 'Total Payable'}
              </span>
              <span style={{ fontSize: '1.45rem', fontWeight: 900, color: '#0284c7' }}>
                ₹{totalAmount.toLocaleString()}
              </span>
            </div>
          </div>

          {/* ========================================================= */}
          {/* DYNAMIC UPI PAYMENT & PRE-FILLED QR CODE */}
          {/* ========================================================= */}
          <div style={{
            margin: '1.25rem 0 0.5rem 0',
            background: 'linear-gradient(180deg, #f0fdf4 0%, #dcfce7 100%)',
            border: '2px solid #86efac',
            borderRadius: 16,
            padding: '1.25rem 1rem',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '0.82rem', fontWeight: 900, color: '#15803d', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <Smartphone size={16} />
              <span>{isMr ? 'UPI द्वारे पेमेंट करा (Google Pay / PhonePe / Paytm):' : 'Pay Instantly via UPI (GPay / PhonePe / Paytm):'}</span>
            </div>

            {/* Dynamic Scannable QR Code */}
            <div style={{ margin: '0.85rem 0' }}>
              <div style={{
                display: 'inline-block',
                background: '#ffffff',
                padding: '8px',
                borderRadius: 14,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: '1px solid #bbf7d0'
              }}>
                <img
                  src={qrCodeUrl}
                  alt="UPI QR Code"
                  style={{ width: 160, height: 160, display: 'block', margin: '0 auto', borderRadius: 8 }}
                />
              </div>
              <div style={{ fontSize: '0.72rem', color: '#15803d', fontWeight: 700, marginTop: 6 }}>
                {isMr ? '📱 कोणत्याही UPI ॲपवरून हा QR कोड स्कॅन करा' : 'Scan this QR code using any UPI App'}
              </div>
            </div>

            {/* Payee Dynamic UPI ID */}
            <div style={{
              background: '#ffffff',
              padding: '0.4rem 0.75rem',
              borderRadius: 8,
              border: '1px dashed #86efac',
              display: 'inline-block',
              fontSize: '0.95rem',
              fontWeight: 900,
              color: '#0369a1',
              fontFamily: 'monospace'
            }}>
              {upiId}
            </div>

            {/* Dynamic Bank Account Details for NEFT/IMPS if enabled */}
            {tenantInfo?.dairy_settings?.print_bank_details === '1' && tenantInfo?.dairy_settings?.bank_account_no && (
              <div style={{
                marginTop: '1rem',
                background: '#ffffff',
                padding: '0.75rem 1rem',
                borderRadius: 10,
                border: '1px solid #bbf7d0',
                fontSize: '0.78rem',
                textAlign: 'left',
                color: '#1e293b'
              }}>
                <div style={{ fontWeight: 800, color: '#15803d', marginBottom: 4 }}>
                  🏦 {isMr ? 'बँक खात्याचे तपशील (NEFT / IMPS):' : 'Bank Transfer (NEFT/IMPS) Details:'}
                </div>
                {tenantInfo.dairy_settings.bank_name && <div><strong>{isMr ? 'बँक:' : 'Bank:'}</strong> {tenantInfo.dairy_settings.bank_name}</div>}
                <div><strong>{isMr ? 'खाते क्र.:' : 'A/C No:'}</strong> <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{tenantInfo.dairy_settings.bank_account_no}</span></div>
                {tenantInfo.dairy_settings.bank_ifsc && <div><strong>IFSC:</strong> <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{tenantInfo.dairy_settings.bank_ifsc}</span></div>}
                {tenantInfo.dairy_settings.bank_holder_name && <div><strong>{isMr ? 'खातेधारकाचे नाव:' : 'Name:'}</strong> {tenantInfo.dairy_settings.bank_holder_name}</div>}
              </div>
            )}

            {/* Direct 1-Click UPI Mobile Payment Button */}
            <div style={{ marginTop: '0.85rem' }} className="no-print">
              <a
                href={upiPayUrl}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  padding: '0.65rem 1.5rem',
                  borderRadius: 10,
                  background: '#16a34a',
                  color: '#ffffff',
                  fontWeight: 900,
                  fontSize: '0.95rem',
                  textDecoration: 'none',
                  boxShadow: '0 4px 6px -1px rgba(22, 163, 74, 0.4)'
                }}
              >
                <span>{isMr ? `UPI ॲपमध्ये उघडा (₹${totalAmount.toLocaleString()} भरा)` : `Open in UPI App (Pay ₹${totalAmount.toLocaleString()})`}</span>
                <ExternalLink size={16} />
              </a>
            </div>
          </div>

          <div style={{ textAlign: 'center', fontSize: '0.75rem', color: '#94a3b8', marginTop: '1rem' }}>
            {isMr ? 'आपल्या सहकार्याबद्दल मनापासून धन्यवाद! 🙏' : 'Thank you for your business! 🙏'}
          </div>

        </div>

      </div>
    </div>
  );
}
